/* eslint-disable radix */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable react/no-array-index-key */
/* eslint-disable no-nested-ternary */
import { useNavigate, useSearchParams } from 'react-router';
import { BadgeInfo, CalendarIcon, Check, ChevronLeft, ChevronsUpDown } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import ReactQuill from 'react-quill';
import {
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import LayoutAdmin from '../../../components/layouts/LayoutAdmin';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../../components/ui/breadcrumb';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../components/ui/form';
import usePartner from '../../../stores/usePartner';
import 'react-quill/dist/quill.bubble.css';
import useAuth from '../../../stores/useAuth';
import { FormSchema } from './Schema';
import Input from '../../../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import cn from '../../../lib/utils';
import { Calendar } from '../../../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../components/ui/hover-card';
import { columns } from './Column';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import useInvoice from '../../../stores/useInvoice';
import { IInvoice, IReceipt, IReceiptDetail } from '../../../types/types';
import useReceipt from '../../../stores/useReceipt';
import { useNavigateBack } from '../../../hooks/useNavigateBack';

export default function CreateReceiptPage() {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [selectedInvoices, setSelectedInvoices] = useState<IInvoice[]>([]);
  const [totalAmountToPay, setTotalAmountToPay] = useState<number>(0);
  const { partners, fetchPartner } = usePartner();
  const { dataUser } = useAuth();
  const navigate = useNavigate();
  const [selectedPartner, setSelectedPartner] = useState('');
  const { invoices, fetchInvoice } = useInvoice();
  const { createReceipt, receipts, loading } = useReceipt();
  const [searchParams] = useSearchParams();
  const partnerId = searchParams.get('partnerId');
  const invoiceId = searchParams.get('invoiceId');

  const navigateBack = useNavigateBack('/receipt');

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      receipt_number: '',
      partner_id: partnerId || '',
      date: new Date(),
      method: undefined,
      received: undefined,
      amount: 0,
      description: '',
      receipt_details: [],
    },
  });

  // Filter invoices based on selectedPartner
  const filteredInvoices = React.useMemo(() => {
    if (!selectedPartner || !invoices) return [];

    return invoices.filter((invoice) => invoice.partner_id === selectedPartner && invoice.status_payment !== 'paid');
  }, [invoices, selectedPartner]);

  useEffect(() => {
    if (partnerId) {
      setSelectedPartner(partnerId);
    }
  }, [partnerId]);

  useEffect(() => {
    const selectedInvoiceRows: Record<string, boolean> = {};

    filteredInvoices.forEach((item, index) => {
      if (invoiceId === item.invoice_id) {
        selectedInvoiceRows[index] = true;
      }
    });

    setRowSelection(selectedInvoiceRows);
  }, [invoiceId, filteredInvoices]);

  const table = useReactTable({
    data: filteredInvoices,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  // Update selected invoices and total amount when row selection changes
  useEffect(() => {
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const selectedInvoiceData = selectedRows.map((row) => row.original as IInvoice);
    setSelectedInvoices(selectedInvoiceData);

    // Calculate total amount from selected invoices
    const total = selectedInvoiceData.reduce((sum, invoice) => sum + invoice.amount_due, 0);
    setTotalAmountToPay(total);

    // Update the form value for amount
    form.setValue('amount', total);

    // Add this: Update receipt_details in the form
    const receipt_details = selectedInvoiceData.map((invoice) => ({
      invoice_id: invoice.invoice_id,
    }));
    form.setValue('receipt_details', receipt_details as IReceiptDetail[]);
  }, [rowSelection, table, form]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    // Your submission logic here
    const dataReceipt: IReceipt = {
      receipt_number: data.receipt_number,
      user_id: dataUser?.user_id as string,
      partner_id: data.partner_id,
      date: data.date,
      method: data.method,
      received: data.received,
      description: data.description,
      amount: data.amount,
      receipt_details: data.receipt_details as IReceiptDetail[],
    };

    try {
      if (!loading) {
        const response = await createReceipt(dataReceipt);
        const { success, message } = response;
        if (!success) {
          throw new Error(message);
        }
        navigate('/receipt');
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    if (!partners) {
      fetchPartner();
    }
  }, [partners, fetchPartner]);

  useEffect(() => {
    if (!invoices) {
      fetchInvoice();
    }
  }, [invoices, fetchInvoice]);

  // Update table data when partner changes
  useEffect(() => {
    // Reset row selection when partner changes
    setRowSelection({});
    // Reset table pagination to first page when filtering changes
    table.resetPageIndex(true);
  }, [selectedPartner, table]);

  // Format currency
  const formatCurrency = (amount: number): string =>
    new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 2,
    }).format(amount);

  useEffect(() => {
    async function generateReceiptNumber() {
      try {
        if (receipts) {
          const currentYear = new Date().getFullYear();
          const yearPrefix = `PYI/${currentYear}/`;

          const currentYearReceipts = receipts.filter(
            (receipt) => receipt.receipt_number && receipt.receipt_number.startsWith(yearPrefix),
          );

          if (currentYearReceipts.length > 0) {
            const sortedReceipts = [...currentYearReceipts].sort((a, b) => {
              const aNumber = parseInt(a.receipt_number?.split('/')[2] || '0');
              const bNumber = parseInt(b.receipt_number?.split('/')[2] || '0');
              return bNumber - aNumber; // Descending order
            });

            const lastReceiptNumber = sortedReceipts[0].receipt_number;
            const lastSequence = parseInt(lastReceiptNumber.split('/')[2] || '0');

            const nextSequence = lastSequence + 1;
            const formattedSequence = String(nextSequence).padStart(4, '0');
            const newReceiptNumber = `PYI/${currentYear}/${formattedSequence}`;

            form.setValue('receipt_number', newReceiptNumber);
          } else {
            form.setValue('receipt_number', `PYI/${currentYear}/0001`);
          }
        } else {
          const currentYear = new Date().getFullYear();
          form.setValue('receipt_number', `PYI/${currentYear}/0001`);
        }
      } catch (error) {
        console.error('Error generating invoice number:', error);
        const currentYear = new Date().getFullYear();
        form.setValue('receipt_number', `PYI/${currentYear}/0001`);
      }
    }

    generateReceiptNumber();
  }, [form, receipts]);

  return (
    <LayoutAdmin header="Receipt">
      <div className="flex gap-4 items-center">
        <button
          type="button"
          className="flex items-center gap-1 text-xs md:text-base cursor-pointer font-medium text-secondary"
          onClick={navigateBack}
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <div className="w-2 h-2 rounded-full bg-slate-100" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/receipt" className="text-xs md:text-base">
                Sales Receipt
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="text-xs md:text-base">Create Sales Receipt</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <p className="md:text-xl text-base font-medium">Receive Payment for Invoices</p>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                name="amount" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem className="hidden">
                    <Input id="amount" {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="partner_id"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel>
                      Payment From <span className="text-red-600">*</span>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            role="combobox"
                            className={cn('justify-between', !field.value && 'text-muted-foreground')}
                          >
                            {field.value
                              ? partners?.find((p) => p.partner_id === field.value)?.name
                              : 'No Client Selected'}
                            <ChevronsUpDown className="opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="p-0">
                        <Command>
                          <CommandInput placeholder="Search partner..." className="h-9" />
                          <CommandList>
                            <CommandEmpty>No partner found.</CommandEmpty>
                            <CommandGroup>
                              {partners?.map((p) => (
                                <CommandItem
                                  value={p.name}
                                  key={p.partner_id}
                                  onSelect={() => {
                                    form.setValue('partner_id', p?.partner_id as string);
                                    setSelectedPartner(p?.partner_id as string);
                                  }}
                                >
                                  {p.name}
                                  <Check
                                    className={cn(
                                      'ml-auto',
                                      p.partner_id === field.value ? 'opacity-100' : 'opacity-0',
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel>
                      Date <span className="text-red-600">*</span>
                    </FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-[240px] pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            {field.value ? format(field.value, 'PPP') : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date: Date) => date < new Date('1900-01-01')}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FormField
                name="receipt_number" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="receipt_number">
                      Receipt No. <span className="text-red-600">*</span>
                    </FormLabel>
                    <Input id="receipt_number" placeholder="Enter receipt number..." {...field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="method" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel className="flex gap-2 items-center">
                      <p>
                        Payment Method <span className="text-red-600">*</span>
                      </p>
                      <HoverCard>
                        <HoverCardTrigger>
                          <BadgeInfo className="w-4 h-4 md:w-5 md:h-5" />
                        </HoverCardTrigger>
                        <HoverCardContent className="text-xs font-normal">
                          Select the payment method that your client used
                        </HoverCardContent>
                      </HoverCard>
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Payment Method" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Digital Payment">Digital Payment</SelectItem>
                        <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                        <SelectItem value="Credit Card">Credit Card</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Memo">Memo</SelectItem>
                        <SelectItem value="Bank">Bank</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="received" // Nama field
                control={form.control} // Kontrol dari useForm
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-1">
                    <FormLabel className="flex gap-2 items-center">
                      <p>
                        To Finance Account <span className="text-red-600">*</span>
                      </p>
                      <HoverCard>
                        <HoverCardTrigger>
                          <BadgeInfo className="w-4 h-4 md:w-5 md:h-5" />
                        </HoverCardTrigger>
                        <HoverCardContent className="text-xs font-normal">
                          Select bank or cash Account where the payment is received
                        </HoverCardContent>
                      </HoverCard>
                    </FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a Finance Account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Kas Kecil">Kas Kecil</SelectItem>
                        <SelectItem value="Bank">Bank</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name="description"
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex gap-1 items-center">
                      <p>Notes</p>
                    </FormLabel>
                    <ReactQuill
                      theme="bubble"
                      placeholder="Enter notes..."
                      className={`${
                        form.formState.errors.description ? 'border-red-500' : ''
                      } h-36 rounded-md border border-neutral-200`}
                      {...field}
                      onChange={(content) => field.onChange(content)}
                    />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <p className="mb-2">To Invoices</p>
                <div className="flex flex-col md:flex-row gap-2 items-center">
                  <Input
                    placeholder="Search by Invoice No."
                    value={(table.getColumn('invoice_number')?.getFilterValue() as string) ?? ''}
                    onChange={(event) => table.getColumn('invoice_number')?.setFilterValue(event.target.value)}
                    className="max-w-sm"
                  />
                </div>
              </div>
              <div className="rounded-md border w-[calc(100dvw-32px)] md:w-full">
                <Table className="overflow-x-scroll">
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {filteredInvoices.length > 0 ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={columns.length} className="h-24 text-center">
                          {selectedPartner ? 'No invoices found for this client.' : 'Please select a client first.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
          <div className="md:w-1/2 w-full my-8 ml-auto">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium">Total amount paid</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(totalAmountToPay)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
          <div className="flex justify-end items-center gap-2 w-full mt-6">
            <Button type="submit" className="mt-4 btn-primary" disabled={selectedInvoices.length === 0 || loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="outline" type="button" className="mt-4 btn-primary" onClick={() => navigate('/receipt')}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </LayoutAdmin>
  );
}
